'use client';
import React, {useState, useEffect, Suspense} from 'react'
import { ThreadAPI } from '../../../data/socialize';
import { useInView } from 'react-intersection-observer';
import { InstrumentAPI } from '../../../data/market';
import { PortfolioAPI, TransactionAPI } from '../../../data/portfolios';
import ThreadCreateForm from '../threads/ThreadCreateForm';
import { EditorialContentAPI } from '../../../data/publications';
import SpinnerPage from '../spinners/SpinnerPage';
import { COUNTRY_UAE_ID } from '../../../data/constants';
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/navigation'
import PortfolioListItem from '../../entities/portfolios/PortfolioListItem';

import PortfolioTransactionListItem from '../../entities/portfolios/PortfolioTransactionListItem';
import InstrumentListSort from '../../entities/instruments/InstrumenListSort';
import InstrumentListSearch from '../../entities/instruments/InstrumentListSearch';
import ECListItem from '../../entities/editorial-contents/ECListItem';
import InstrumentListItem from '../../entities/instruments/InstrumentListItem';
import ThreadListItem from '../../entities/threads/ThreadListItem';
import PortfolioListSort from '../../entities/portfolios/PortfolioListSort';


export default function LoadMoreListClient(props) {
  const {
    list, 
    listItemComponent,
    listItemProps={},
    sortComponent,
    searchComponent,
    sortDefault=null,
    entity,
    entityId=null,
    limit=10,
    offset=0,
    showCreateForm=false,
    appName=null,
    modelName=null,
    createLabel,
    createPlaceholder,
    defaultQuery=null,
    countryId=COUNTRY_UAE_ID,
  } = props

  let ListItemComponent = null
  if(listItemComponent == 'ThreadListItem') {
    ListItemComponent = ThreadListItem
  } else if (listItemComponent == 'EditorialContentListItem') {
    ListItemComponent = ECListItem
  } else if (listItemComponent == 'InstrumentListItem') {
    ListItemComponent = InstrumentListItem
  } 
  else if(listItemComponent == 'PortfolioListItem') {
    ListItemComponent = PortfolioListItem
  } 
  else if(listItemComponent == 'PortfolioTransactionListItem') {
    ListItemComponent = PortfolioTransactionListItem
  } else {
    ListItemComponent = ThreadListItem
  }

  let SortComponent = null
  if(sortComponent == 'InstrumentListSort') {
    SortComponent = InstrumentListSort
  } else if(sortComponent == 'PortfolioListSort') {
    SortComponent = PortfolioListSort
  }

  let SearchComponent = null
  if(searchComponent == 'InstrumentListSearch') {
    SearchComponent = InstrumentListSearch
  }

  const router = useRouter()

  const searchParams = useSearchParams()
  const urlParams = {
    offset: parseInt(searchParams.get('offset')),
    limit: parseInt(searchParams.get('limit')),
    sortBy: searchParams.get('sortBy'),
    query: searchParams.get('query'),
  }

  const [isLoading, setIsLoading] = useState(false)
  const [hasNewData, setHasNewData] = useState(true)
  const [currentOffset, setCurrentOffset] = useState(urlParams.offset || offset)
  // const [currentLimit, setCurrentLimit] = useState(limit)
  const [currentList, setCurrentList] = useState(list)
  const [sortBy, setSortBy] = useState(urlParams.sortBy || sortDefault)
  const [query, setQuery] = useState(urlParams.query || defaultQuery)

  const { ref, inView, entry } = useInView({
    /* Optional options */
    rootMargin: '500px',
    threshold: 0,
  });

  useEffect(() => {
    if(inView && !isLoading) {
      handleLoadMore()
    }
  }, [inView])

  useEffect(() => {
    if(sortBy) {
      reloadList()
    }
  }, [sortBy])

  useEffect(() => {
    if(currentOffset != urlParams.offset && !isLoading) {
      reloadList()
    }
  }, [currentOffset])
  

  useEffect(() => {
    if(query && query != "" && query.length > 2) {
      reloadList()
    } else if(query == "") {
      reloadList()
    }
  }, [query])

  const reloadList = async () => {
    setIsLoading(true)
    const offset = urlParams.offset || 0
    setCurrentOffset(offset)
    const data = await fetchData(offset)
    setCurrentList(data)
    setIsLoading(false)
  }

  const fetchData = async (newOffset) => {
    let data = []
    if(entity === 'Threads') {
      data = await ThreadAPI.getList({
        offset: newOffset, limit, is_default_transaction_thread: 'false'
      })
    } 
    else if(entity === 'ThreadsOnStocks') {
      data = await ThreadAPI.getConnectedToStockList({offset: newOffset, limit})
    } 
    else if(entity === 'ThreadsOnPortfolios') {
      data = await ThreadAPI.getConnectedToPortfolioList({offset: newOffset, limit})
    } 
    else if(entity === 'ThreadsOnPublications') {
      data = await ThreadAPI.getConnectedToPublicationList({offset: newOffset, limit})
    } 
    else if(entity === 'ThreadsOnTransactions') {
      data = await ThreadAPI.getConnectedToTransactionList({offset: newOffset, limit})
    }
    else if(entity === 'ThreadsOnInstrumentPublications') {
      data = await ThreadAPI.getInstrumentPublicationList({instrument_id: entityId, country_id: countryId, offset: newOffset, limit})
    } 
    else if(entity === 'ThreadsPortfolio') {
      data = await ThreadAPI.getPortfolioThreadList({portfolio_id: entityId, offset: newOffset, limit})
    } 
    else if(entity === 'ThreadsPortfolioWall') {
      data = await ThreadAPI.getPortfolioThreadWallList({portfolio_id: entityId, offset: newOffset, limit})
    } 
    else if(entity === 'ThreadsInstrumentWall') {
      data = await ThreadAPI.getInstrumentWallThreadList({country_id: countryId, instrument_id: entityId, offset: newOffset, limit})
    } 
    else if(entity === 'Instrument') {
      data = await InstrumentAPI.getList({country_id: entityId, offset: newOffset, limit, sort_by: sortBy, query})
    } 
    else if(entity === 'MarketInstrument') {
      data = await InstrumentAPI.getList({country_id: COUNTRY_UAE_ID, offset: newOffset, limit, sortBy, query, market_slug: entityId})
    }
    else if(entity === 'TransactionPortfolio') {
      data = await TransactionAPI.getList({portfolio_identifier: entityId, offset: newOffset, limit})
    } 
    else if(entity === 'Portfolio') {
      data = await PortfolioAPI.getList({country_id: entityId, offset: newOffset, limit, sort_by: sortBy})
    } 
    else if(entity === 'Article') {
      data = await EditorialContentAPI.getList({
        country_id: countryId, instrument_id: entityId, offset: newOffset, limit
      })
    }
    
    return data
  }

  const handleLoadMore = async () => {
    if(!hasNewData) {
      return
    }
    setIsLoading(true)
    
    const newOffset = currentOffset + limit
    setCurrentOffset(newOffset)
    const data = await fetchData(newOffset)
    setCurrentList(currentList => [...currentList, ...data])
    
    if (data.length == 0) {
      setHasNewData(false)
    } else {
      setHasNewData(true)
      router.push(`?offset=${newOffset}&query=${query}`, {scroll: false})
    }
    setIsLoading(false)
  }

  return (
    <>
      {showCreateForm && entityId && (
        <Suspense>
          <ThreadCreateForm 
            entityId={entityId} onSuccess={reloadList}
            appName={appName} modelName={modelName}
            createLabel={createLabel} createPlaceholder={createPlaceholder}
          />
        </Suspense>
      )}
      {SortComponent && <SortComponent sortBy={sortBy} setSortBy={setSortBy} />}
      {SearchComponent && <SearchComponent query={query} setQuery={setQuery}/>}

      {currentList.map((item, index) => {
        const key = item.identifier || index
        return (
          <ListItemComponent key={key} index={index} item={item} {...listItemProps} />
        )
      })}
      {!isLoading && hasNewData && (
        <button 
          ref={ref}
          onClick={handleLoadMore} 
          className="btn btn-primary"
        > Load More </button>
      )}
      {isLoading && (
          <div className="col-span-full">
            <SpinnerPage color="primary" text="Loading..." />
          </div>
        )}
      
      {!hasNewData && (
        <div className="col-span-full">
          <p className="text-center text-gray-500 text-sm">No more data to load</p>
        </div>
      )}
    </>
  )
}
